import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, Edit, AlertTriangle, Calendar, Droplets, ClipboardList } from 'lucide-react';
import TaskPhotoUpload from '@/components/TaskPhotoUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TodayActionTasks = ({ showHarvest = true, compact = false }) => {
  const [todayWork, setTodayWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDateAdjustModal, setShowDateAdjustModal] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [delayReason, setDelayReason] = useState('');
  const [completeModal, setCompleteModal] = useState(null);
  const [completePhotoId, setCompletePhotoId] = useState(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchTodayWork();
  }, []);

  const fetchTodayWork = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/supervisor/today-work`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setTodayWork(res.data);
    } catch (error) {
      console.error('Failed to fetch today work:', error);
      toast.error('Failed to load today\'s tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async () => {
    const task = completeModal;
    if (!task) return;
    // Warn (but allow) when a photo-recommended task is being completed without a photo
    const photoTask = /casing|covering|ruffling/i.test(task.task || '');
    if (photoTask && !completePhotoId) {
      const ok = window.confirm(`A photo is recommended for "${task.task}". Complete without a photo?`);
      if (!ok) return;
    }
    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/room-cycles/${task.room_cycle_id}/complete-task`,
        { day: task.day_number, notes: completeNotes || null, photo_id: completePhotoId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Task "${task.task}" completed for ${task.room_name}`);
      setCompleteModal(null);
      setCompletePhotoId(null);
      setCompleteNotes('');
      fetchTodayWork();
    } catch (error) {
      toast.error('Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  const openCompleteModal = (task) => {
    setCompletePhotoId(null);
    setCompleteNotes('');
    setCompleteModal(task);
  };

  const handleDateAdjust = async () => {
    if (!showDateAdjustModal || !newDate) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/room-cycles/${showDateAdjustModal.room_cycle_id}/update-date`, 
        {
          date_field: showDateAdjustModal.date_field,
          new_date: newDate,
          delay_reason: delayReason || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Date adjusted successfully');
      setShowDateAdjustModal(null);
      setNewDate('');
      setDelayReason('');
      fetchTodayWork();
    } catch (error) {
      console.error('Failed to adjust date:', error);
      toast.error('Failed to adjust date');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading today's tasks...</div>;
  }

  if (!todayWork) {
    return null;
  }

  const getTaskBadgeColor = (task) => {
    switch (task.task.toLowerCase()) {
      case 'casing': return 'bg-blue-100 text-blue-800';
      case 'ruffling': return 'bg-indigo-100 text-indigo-800';
      case 'covering': return 'bg-teal-100 text-teal-800';
      case 'airing start': return 'bg-lime-100 text-lime-800';
      case 'room empty': return 'bg-gray-100 text-gray-800';
      default: return 'bg-orange-100 text-orange-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Tasks Section */}
      {todayWork.action_tasks && todayWork.action_tasks.length > 0 && (
        <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <ClipboardList className="w-5 h-5" />
              Today's Action Tasks ({todayWork.action_tasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayWork.action_tasks.map((task, idx) => (
              <div
                key={task.room_cycle_id ? `${task.room_cycle_id}-${task.task}` : `${task.room_name}-${idx}`}
                className={`p-4 rounded-lg border-2 ${
                  task.completed ? 'bg-green-50 border-green-300' :
                  task.is_overdue ? 'bg-red-50 border-red-400' :
                  'bg-white border-orange-200'
                }`}
                data-testid={`action-task-${idx}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-lg font-bold text-gray-900">{task.room_name}</span>
                      <Badge className={getTaskBadgeColor(task)}>
                        {task.task}
                      </Badge>
                      {task.is_overdue && !task.completed && (
                        <Badge className="bg-red-600 text-white animate-pulse" data-testid={`overdue-badge-${idx}`}>
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Overdue {task.days_overdue} day{task.days_overdue === 1 ? '' : 's'}
                        </Badge>
                      )}
                      {task.completed && <CheckCircle className="w-5 h-5 text-green-600" />}
                    </div>
                    <p className="text-sm text-gray-600">{task.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Batch: {task.batch_name} | Day {task.current_day || task.day_number}/45
                      {task.scheduled_date && (
                        <span className={`ml-2 font-medium ${task.is_overdue ? 'text-red-600' : 'text-blue-600'}`}>
                          | Scheduled: {format(new Date(task.scheduled_date), 'MMM d, yyyy')}
                        </span>
                      )}
                    </p>
                  </div>
                  {!compact && !task.completed && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDateAdjustModal(task)}
                        data-testid={`adjust-date-${idx}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Adjust
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                        onClick={() => openCompleteModal(task)}
                        data-testid={`complete-task-${idx}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Complete
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Harvest Section */}
      {showHarvest && todayWork.production_rooms && todayWork.production_rooms.length > 0 && (
        <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <Droplets className="w-5 h-5" />
              Today's Harvest ({todayWork.production_rooms.length} rooms)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayWork.production_rooms.map((room, idx) => (
              <div
                key={room.room_cycle_id || `${room.room_name}-${idx}`}
                className="p-4 rounded-lg border-2 bg-white border-yellow-200"
                data-testid={`harvest-room-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg font-bold text-gray-900">{room.room_name}</span>
                      <Badge className="bg-yellow-100 text-yellow-800">
                        {room.flush} Day {room.day_in_flush}
                      </Badge>
                      {room.is_delayed && (
                        <Badge className="bg-red-100 text-red-800">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Delayed
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Batch: {room.batch_name} | Day {room.current_day}/45
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No Tasks Message */}
      {(!todayWork.action_tasks || todayWork.action_tasks.length === 0) && 
       (!showHarvest || !todayWork.production_rooms || todayWork.production_rooms.length === 0) && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-green-800">No Action Tasks Today</h3>
            <p className="text-sm text-green-600 mt-1">All rooms are running smoothly.</p>
          </CardContent>
        </Card>
      )}

      {/* Complete Task Modal (with optional photo) */}
      <Dialog open={!!completeModal} onOpenChange={(o) => { if (!o) setCompleteModal(null); }}>
        <DialogContent data-testid="complete-task-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#1A4D2E]" />
              Complete: {completeModal?.task} — {completeModal?.room_name}
            </DialogTitle>
            <DialogDescription className="sr-only">Attach an optional photo and notes, then mark the task complete.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block">Photo of the room (recommended)</Label>
              <p className="text-xs text-gray-500 mb-2">Snap a picture of the finished room so the Grower & Owner can see it.</p>
              <TaskPhotoUpload onUploaded={setCompletePhotoId} testIdPrefix="complete" />
            </div>
            <div>
              <Label htmlFor="complete-notes">Notes (optional)</Label>
              <Textarea
                id="complete-notes"
                data-testid="complete-task-notes"
                placeholder="Any observations about this task..."
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteModal(null)}>Cancel</Button>
            <Button
              className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              onClick={handleCompleteTask}
              disabled={completing}
              data-testid="confirm-complete-task"
            >
              {completing ? 'Completing...' : 'Mark Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Adjust Modal */}
      <Dialog open={!!showDateAdjustModal} onOpenChange={() => setShowDateAdjustModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Adjust Date - {showDateAdjustModal?.room_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Task</Label>
              <p className="text-sm text-gray-600">{showDateAdjustModal?.task}</p>
            </div>
            <div>
              <Label>Current Date</Label>
              <p className="text-sm text-gray-600">
                {showDateAdjustModal?.scheduled_date 
                  ? format(new Date(showDateAdjustModal.scheduled_date), 'MMMM d, yyyy')
                  : 'Not set'}
              </p>
            </div>
            <div>
              <Label htmlFor="new-date">New Date</Label>
              <Input
                id="new-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="delay-reason">Reason (if delaying)</Label>
              <Textarea
                id="delay-reason"
                placeholder="Enter reason for date change..."
                value={delayReason}
                onChange={(e) => setDelayReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDateAdjustModal(null)}>Cancel</Button>
            <Button 
              className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              onClick={handleDateAdjust}
              disabled={!newDate}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TodayActionTasks;
